import { Checkbox, Col, Empty, Image, Row, Select, Spin, Timeline } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { MdOutlineBedtime, MdOutlineRestaurant, MdOutlineWc } from "react-icons/md";
import { useQuery } from "react-query";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { cloudFrontUrl, serverUrl } from "../../../../../nestserver";

const { RangePicker } = DatePicker;
const { Option } = Select;

export const TYPE_META = {
  meal: { color: "orange", label: "Meal", icon: MdOutlineRestaurant },
  loo: { color: "blue", label: "Loo", icon: MdOutlineWc },
  sleep: { color: "purple", label: "Sleep", icon: MdOutlineBedtime },
};

const entrySubtitle = (entry) => {
  const parts = [];
  if (entry.type === "meal") {
    if (entry.meal_slot) parts.push(entry.meal_slot);
    if (entry.food) parts.push(entry.food);
    if (entry.amount) parts.push(`Ate: ${entry.amount}`);
  } else if (entry.type === "loo") {
    if (entry.loo_kind) parts.push(entry.loo_kind);
  } else if (entry.type === "sleep") {
    parts.push(
      entry.sleep_end && entry.duration_min
        ? `${entry.duration_min} min nap`
        : "Nap started",
    );
  }
  if (entry.note) parts.push(entry.note);
  return parts.join(" • ");
};

/** Read-only per-day/range timeline of a child's meals, loo visits and naps. */
const StudentDailyActivity = () => {
  const [selectedStudentId, setSelectedStudentId] = useState();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mutedTypes, setMutedTypes] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: moment().subtract(6, "days"),
    to: moment(),
  });

  async function fetchStudents() {
    const res = await axios.get(`${serverUrl}/students/by-parent/`, {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data.result;
  }
  const students = useQuery("students/by-parent", fetchStudents);

  useEffect(() => {
    if (students?.data?.length > 0 && !selectedStudentId) {
      setSelectedStudentId(students.data[0]._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students?.data]);

  useEffect(() => {
    if (!selectedStudentId || !dateRange.from || !dateRange.to) return;
    const fromDate = dateRange.from.format("YYYY-MM-DD");
    const toDate = dateRange.to.format("YYYY-MM-DD");
    setLoading(true);
    axios
      .get(
        `${serverUrl}/montessori/daily-activity/student/${selectedStudentId}?fromDate=${fromDate}&toDate=${toDate}`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      )
      .then((res) => {
        setEntries(res.data?.result?.data || []);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [selectedStudentId, dateRange]);

  const visibleEntries = entries.filter((e) => !mutedTypes.includes(e.type));

  return (
    <div className="daily-activity pt-8">
      <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
        <div className="text-2xl text-orange-500 font-semibold mb-5">Day Care Activity</div>
        {/* Filter toolbar */}
        <Row gutter={[24, 16]} align="middle" className="pb-6 mb-6 border-b border-gray-100">
          <Col xs={24} md={10} lg={8}>
            <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1.5">
              Child
            </div>
            <Select
              showSearch
              placeholder="Select child"
              className="w-full"
              value={selectedStudentId}
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value) => setSelectedStudentId(value)}
            >
              {students?.data?.map((data) => (
                <Option key={data._id} value={data._id}>
                  {`${data?.first_name} ${data?.last_name}`}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={14} lg={10}>
            <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1.5">
              Date range
            </div>
            <RangePicker
              format="DD/MM/YYYY"
              value={[dateRange.from, dateRange.to]}
              disabledDate={(d) => !d || d.isAfter(moment().endOf("day"))}
              className="w-full"
              onChange={(dates) => {
                if (dates) setDateRange({ from: dates[0], to: dates[1] });
              }}
            />
          </Col>
          <Col xs={24} lg={6}>
            <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1.5">
              Show
            </div>
            <Checkbox.Group
              value={Object.keys(TYPE_META).filter((t) => !mutedTypes.includes(t))}
              onChange={(checked) =>
                setMutedTypes(Object.keys(TYPE_META).filter((t) => !checked.includes(t)))
              }
              options={Object.entries(TYPE_META).map(([value, meta]) => ({
                label: (
                  <span className="inline-flex items-center gap-1">
                    <meta.icon size={15} />
                    {meta.label}
                  </span>
                ),
                value,
              }))}
            />
          </Col>
        </Row>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Spin />
          </div>
        ) : visibleEntries.length === 0 ? (
          <div className="py-10">
            <Empty description="No activity logged for this range" />
          </div>
        ) : (
          <Timeline mode="left" className="pt-2">
            {visibleEntries.map((entry) => {
              const Icon = TYPE_META[entry.type]?.icon;
              return (
                <Timeline.Item key={entry._id} color={TYPE_META[entry.type]?.color}>
                  <div className="font-semibold inline-flex items-center gap-1.5">
                    {Icon && <Icon size={16} />}
                    {TYPE_META[entry.type]?.label || entry.type}
                    <span className="text-gray-400 font-normal ml-1">
                      {moment(entry.occurred_at).format("DD MMM, h:mm A")}
                    </span>
                  </div>
                  <div className="text-gray-600 text-sm">{entrySubtitle(entry)}</div>
                  {entry.photo && (
                    <Image
                      src={`${cloudFrontUrl}/${selectedStudentId}/${entry.photo}`}
                      width={56}
                      height={56}
                      style={{ objectFit: "cover", borderRadius: 8, marginTop: 6 }}
                    />
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        )}
      </div>
    </div>
  );
};

export default StudentDailyActivity;
